import { DesignSystemProvider, OfflineIndicator, ToastProvider } from '@ops-matrix/design-system-ui';
import { TracingProvider } from '@ops-matrix/design-system-ui/tracing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ConsumerAstorProvider } from './components/astor/ConsumerAstorProvider';
import { CommandPaletteProvider } from './components/CommandPalette';
import { env } from './config/env';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { ConsumerAuthProvider } from './contexts/ConsumerAuthContext';
import { ConsumerTypeProvider } from './contexts/ConsumerTypeContext';
import { CustomDomainProvider } from './contexts/CustomDomainContext';
import { PermissionProviderWrapper } from './contexts/PermissionProviderWrapper';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import { TRPCProviderInternal } from './lib/trpc/provider';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      gcTime: 300000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Browser tracing configuration (Issue #469)
 * Connects frontend spans to backend OpenTelemetry infrastructure
 *
 * Note: Enabled defaults to true. Set VITE_OTEL_ENABLED=false to disable.
 */
const tracingConfig = {
  serviceName: 'consumer-portal',
  serviceVersion: '1.0.0',
  collectorUrl: env.VITE_OTEL_COLLECTOR_URL,
  // Only enable if collector URL is explicitly set (not localhost fallback) and not disabled
  // This prevents browser tracing errors in staging/production where no collector is available
  enabled: !!env.VITE_OTEL_COLLECTOR_URL && import.meta.env.VITE_OTEL_ENABLED !== 'false',
  sampleRate: env.VITE_OTEL_SAMPLE_RATE ?? (import.meta.env.DEV ? 1.0 : 0.1),
  propagateTraceHeaderOrigins: [env.VITE_API_URL],
};

// Fade out the HTML splash screen once React mounts
const splash = document.getElementById('splash');
if (splash) {
  splash.classList.add('fade-out');
  setTimeout(() => splash.remove(), 400);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <CustomDomainProvider>
      <TracingProvider config={tracingConfig}>
        <QueryClientProvider client={queryClient}>
          <TRPCProviderInternal>
            <BrowserRouter>
              <BreadcrumbProvider>
                <DesignSystemProvider defaultMode="system" portalType="consumer" designGuideline="material">
                  <ToastProvider position="top-right" defaultDuration={4000}>
                    <ConsumerAuthProvider>
                      <PushNotificationProvider>
                        <PermissionProviderWrapper>
                          <ConsumerTypeProvider>
                            <CommandPaletteProvider>
                              <ConsumerAstorProvider>
                                <OfflineIndicator variant="banner" position="top" />
                                <App />
                              </ConsumerAstorProvider>
                            </CommandPaletteProvider>
                          </ConsumerTypeProvider>
                        </PermissionProviderWrapper>
                      </PushNotificationProvider>
                    </ConsumerAuthProvider>
                  </ToastProvider>
                </DesignSystemProvider>
              </BreadcrumbProvider>
            </BrowserRouter>
          </TRPCProviderInternal>
        </QueryClientProvider>
      </TracingProvider>
    </CustomDomainProvider>
  </React.StrictMode>,
);
